// Apolo IDE — PlanCreateBlockMenu (sprint plan-tab-v1, E2).
//
// Floating menu pra criar block. Aparece em double-click no canvas OU via
// botão "Novo bloco" da toolbar. Lista todos os kinds com ícone + label.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { motion } from 'framer-motion';
import { BLOCK_KINDS, BLOCK_KIND_ORDER } from '../../lib/plan/blockKinds.js';

const MENU_WIDTH = 260;
const ROW_HEIGHT = 36;
const GAP = 8;

export default function PlanCreateBlockMenu({ screenPos, onClose, onPick }) {
  const ref = useRef(null);
  // Auto-flip: abre pra baixo por padrão; se não couber (ex: botão "Novo
  // bloco" na toolbar inferior), abre pra cima. Medido após mount.
  const [placement, setPlacement] = useState({ top: screenPos.y + GAP, ready: false });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const menuH = el.offsetHeight || 480;
    const containerH = el.offsetParent?.clientHeight ?? window.innerHeight;
    let top = screenPos.y + GAP;
    if (top + menuH > containerH - GAP) {
      // não cabe embaixo → abre pra cima a partir do ponto
      top = Math.max(GAP, screenPos.y - menuH - GAP);
    }
    setPlacement({ top, ready: true });
  }, [screenPos.x, screenPos.y]);

  useEffect(() => {
    function outside(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function esc(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
      role="menu"
      aria-label="Criar bloco"
      className="absolute z-40 overflow-hidden rounded-xl border border-apolo-plan-accentBorder bg-apolo-surface1/95 backdrop-blur-md"
      style={{
        left: Math.max(8, Math.min(screenPos.x, window.innerWidth - MENU_WIDTH - 8)),
        top: placement.top,
        width: MENU_WIDTH,
        visibility: placement.ready ? 'visible' : 'hidden',
        boxShadow: 'var(--apolo-plan-glow-wide), 0 10px 30px rgba(0,0,0,0.25)',
      }}
    >
      <div className="border-b border-apolo-borderSubtle px-3 py-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-apolo-textTertiary">Adicionar bloco</span>
      </div>
      <ul role="none" className="max-h-[440px] overflow-y-auto p-1.5">
        {BLOCK_KIND_ORDER.map((kind) => {
          const meta = BLOCK_KINDS[kind];
          const Icon = Icons[meta.icon] || Icons.Square;
          return (
            <li key={kind} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => onPick(kind)}
                className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition hover:bg-apolo-plan-accentMuted focus:bg-apolo-plan-accentMuted focus:outline-none"
                style={{ height: ROW_HEIGHT }}
              >
                <span
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                  style={{ background: `${meta.color}22`, color: meta.color }}
                  aria-hidden="true"
                >
                  <Icon size={14} />
                </span>
                <span className="flex flex-1 flex-col leading-tight">
                  <span className="text-xs font-medium text-apolo-textPrimary">{meta.label}</span>
                  <span className="truncate text-[10px] text-apolo-textTertiary">{meta.description}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}
